import { useMemo, useCallback } from "react"
import {
  Avatar,
  Box,
  Card,
  CardMedia,
  Chip,
  Typography,
  useTheme,
} from "@mui/material"
import { deepmerge } from "@mui/utils"
import { useNavigate } from "react-router-dom"
import { getFeatureDetail, IOccupantDetail } from "utils/feature"
import { useTranslation } from "providers/i18n"
import { DiningIcon, StarIcon } from "components/icons"
import { getIcon } from "utils/icon"
import { hasVatRefund } from "extensions/iconsiam/utils"
import { ComingSoonChip, NewShopChip, VatRefundChip } from "../../chips"

export const DiningCard = ({
  data,
  isNewShop = false,
  isComingSoon = false,
  sx,
  ...props
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const {
    name,
    logo,
    level,
    venueLogo,
    venueRef,
    featuredImage,
    isFeatured,
    isAd,
    allLevels,
  } = useMemo(() => getFeatureDetail(data) as IOccupantDetail, [data])
  const isVatRefund = useMemo(() => hasVatRefund(data), [data])
  const handleClick = useCallback(
    () => navigate(`/maps/place/${data.id}`),
    [navigate, data?.id]
  )

  const theme = useTheme()
  const { styleOverrides = {} } = theme.components?.[`DiningCard`] || {}
  const {
    root,
    featuredImage: featuredImageStyle,
    cardContent,
    levelAvatar,
    venueLogo: venueLogoStyle,
    titleText,
    detailWrapper,
    infoContainer,
    logo: logoStyle,
    chip,
    chipContainer,
    adsChip,
    newShopChip,
    comingSoonChip,
    vatRefundChip,
    multiLevelContainer,
    multiLevelAvatar,
  } = styleOverrides

  const emptyDiningIcon = useMemo(
    () => getIcon(theme?.assets?.empty?.dining) || <DiningIcon />,
    [theme]
  )

  const hasMultipleLevel = allLevels && allLevels.length > 1

  return (
    <Card sx={deepmerge(root, sx)} onClick={handleClick} {...props}>
      <Box sx={featuredImageStyle}>
        <Box sx={chipContainer}>
          {isFeatured && !isAd ? (
            <Chip label={<StarIcon />} size="small" sx={chip} />
          ) : null}
          {isAd && <Chip label={t("sponsored.search_result")} sx={adsChip} />}
          {isNewShop && <NewShopChip sx={newShopChip} />}
          {isComingSoon && <ComingSoonChip sx={comingSoonChip} />}
        </Box>
        {isVatRefund && <VatRefundChip sx={vatRefundChip} />}
        <Avatar src={featuredImage}>{!featuredImage && emptyDiningIcon}</Avatar>
      </Box>
      <Box sx={cardContent}>
        <Box sx={detailWrapper}>
          <Avatar src={logo} sx={logoStyle}>
            {!logo && emptyDiningIcon}
          </Avatar>
          <Typography sx={titleText}>{t(name)}</Typography>
        </Box>
        <Box sx={infoContainer}>
          <CardMedia component="img" image={venueLogo} sx={venueLogoStyle} />
          {hasMultipleLevel ? (
            <Box
              className="DirectoryOccupantCard-multiLevelContainer"
              sx={multiLevelContainer}
            >
              {allLevels.map((levelName, index) => (
                <Box key={index} sx={multiLevelAvatar}>
                  {levelName}
                </Box>
              ))}
            </Box>
          ) : (
            <Avatar sx={levelAvatar} className={venueRef}>
              {t(level)}
            </Avatar>
          )}
        </Box>
      </Box>
    </Card>
  )
}
